import { useSelector } from 'react-redux';
import type { RootState } from '@/app/store';

import Title from '@/components/ui/Title';
import { Award, BadgePercent, Trash2, Trophy } from 'lucide-react';
import ColorSelector from '@/components/ui/ColorSelector';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { UrlVariableInput } from '@/components/ui/UrlVariableInput';
import { useForm } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import z from 'zod';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useTranslation } from 'react-i18next';
import {
  createOfferTemplate,
  deleteOfferTemplate,
  listOfferTemplates,
  updateOfferTemplate,
  uploadOfferTemplateContentImage,
  upsertOfferTemplateContent,
} from '@/features/webinar/webinarThunks';
import { saveCreatedOffer } from '@/lib/offerCreationRollback';
import type {
  OfferTemplate,
  OfferTemplateContentInput,
  OfferTemplateInput,
} from '@/features/webinar/types';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';

interface OfferThankyouFormProps {
  displayOffer?: boolean;
  isEdit?: boolean;
  isEvergreen?: boolean;
  onOfferSaved?: (offerId: number) => void | Promise<void>;
  prefillOffer?: OfferTemplate;
  isExistingOfferMode?: boolean;
}

export default function OfferThankyouForm({
  displayOffer = false,
  isEdit = false,
  isEvergreen,
  onOfferSaved,
  prefillOffer,
  isExistingOfferMode = false,
}: OfferThankyouFormProps) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const visualIdentity = useSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentOffer, setCurrentOffer] = useState<OfferTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string>(isEdit ? '' : '');

  const schema = useMemo(
    () =>
      z.object({
        privateTitle: z
          .string()
          .min(1, t('offerThankyouPage.validation.privateTitleRequired'))
          .refine((val) => val.trim().length > 0, {
            message: t('offerThankyouPage.validation.privateTitleRequired'),
          })
          .refine((val) => val.length <= 200, {
            message: t('offerThankyouPage.validation.privateTitleMax', { max: 200 }),
          }),
        title: z
          .string()
          .min(1, t('offerThankyouPage.validation.titleRequired'))
          .max(35, t('offerThankyouPage.validation.titleMax', { max: 35 }))
          .refine((val) => val.trim().length > 0, {
            message: t('offerThankyouPage.validation.titleRequired'),
          }),
        subtitle: z
          .string()
          .max(50, t('offerThankyouPage.validation.subtitleMax', { max: 50 }))
          .optional()
          .refine((val) => !val || val.trim().length === 0 || val.trim().length > 0, {
            message: t('offerThankyouPage.validation.subtitleNotEmpty'),
          }),
        description: z
          .string()
          .max(100, t('offerThankyouPage.validation.descriptionMax', { max: 100 }))
          .optional()
          .refine((val) => !val || val.trim().length === 0 || val.trim().length > 0, {
            message: t('offerThankyouPage.validation.descriptionNotEmpty'),
          }),
        buttonText: z
          .string()
          .min(1, t('offerThankyouPage.validation.buttonTextRequired'))
          .max(35, t('offerThankyouPage.validation.buttonTextMax', { max: 35 }))
          .refine((val) => val.trim().length > 0, {
            message: t('offerThankyouPage.validation.buttonTextRequired'),
          }),
        buttonLink: z
          .string()
          .min(1, t('offerThankyouPage.validation.buttonLinkRequired'))
          .refine((val) => val.trim().length > 0, {
            message: t('offerThankyouPage.validation.buttonLinkRequired'),
          })
          .refine((val) => val.length <= 1024, {
            message: t('offerThankyouPage.validation.buttonLinkMax', { max: 1024 }),
          })
          .refine((val) => val.trim().startsWith('https://'), {
            message: t('offerThankyouPage.validation.buttonLinkInvalidUrl'),
          }),
        showImage: z.boolean().default(false).optional(),
        img: z.union([z.instanceof(File), z.string()]).optional(),
        showIcon: z.boolean().default(false).optional(),
        icon: z.string().optional(),

        backgroundColor: z.string().optional(),
        customBackgroundColor: z.string().optional(),

        borderColor: z.string().optional(),
        customBorderColor: z.string().optional(),

        buttonBackgroundColor: z.string().optional(),
        customButtonBackgroundColor: z.string().optional(),

        titleColor: z.string().optional(),
        textColor: z.string().optional(),
        buttonTextColor: z.string().optional(),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      privateTitle: '',
      title: '',
      subtitle: '',
      description: '',
      buttonText: '',
      buttonLink: '',
      showImage: false,
      img: undefined,
      showIcon: false,
      icon: '',

      backgroundColor: 'background',
      customBackgroundColor: '#000000',

      borderColor: 'contrast',
      customBorderColor: '#000000',

      buttonBackgroundColor: 'contrast',
      customButtonBackgroundColor: '#000000',

      titleColor: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
      textColor: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000',
      buttonTextColor: '#000000',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    const loadThankYouOffer = async () => {
      if (!workspaceId || !webinarIdParam) {
        setIsLoading(false);
        return;
      }

      const webinarId = parseInt(webinarIdParam, 10);
      if (isNaN(webinarId)) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await dispatch(
          listOfferTemplates({ workspaceId, webinarId, page: 0, size: 50 }),
        ).unwrap();

        const thankYouOffer = response.find((offer) => offer.thankYouPageOffer === true);

        if (thankYouOffer && !isExistingOfferMode) {
          setCurrentOffer(thankYouOffer);

          {
            const content = thankYouOffer.contents?.[0];

            if (content) {
              const resolvedBackgroundColor = resolveColorIdFromValue(
                content.backgroundColor,
                false,
              );
              const resolvedBorderColor = resolveColorIdFromValue(content.borderColor, true);
              const resolvedButtonColor = resolveColorIdFromValue(content.buttonColor, false);

              form.reset({
                privateTitle: thankYouOffer.privateTitle || '',
                title: content.title || '',
                subtitle: content.subTitle || '',
                description: content.description || '',
                buttonText: content.buttonLabel || '',
                buttonLink: content.buttonLink || '',
                showImage: !!content.imageUrl || !!content.imagePath,
                img:
                  content.imageUrl || content.imagePath
                    ? ((content.imageUrl || content.imagePath) as any)
                    : undefined,
                showIcon: !!content.icon,
                icon: content.icon || '',
                backgroundColor: resolvedBackgroundColor.colorId,
                customBackgroundColor: resolvedBackgroundColor.customColor,
                borderColor: resolvedBorderColor.colorId,
                customBorderColor: resolvedBorderColor.customColor,
                buttonBackgroundColor: resolvedButtonColor.colorId,
                customButtonBackgroundColor: resolvedButtonColor.customColor,
                titleColor: content.titleColor || '#000000',
                textColor: content.textColor || '#000000',
                buttonTextColor: content.buttonTextColor || '#000000',
              });

              if (content.imageUrl || content.imagePath) {
                form.setValue('img', (content.imageUrl || content.imagePath) as any);
              }
              if (content.buttonLink && !content.buttonLink.trim().startsWith('https://')) {
                form.setError('buttonLink', {
                  type: 'manual',
                  message: t('offerThankyouPage.validation.buttonLinkInvalidUrl'),
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading thank you offer:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadThankYouOffer();
  }, [workspaceId, webinarIdParam, dispatch, form]);

  useEffect(() => {
    if (!prefillOffer) return;
    const content = prefillOffer.contents?.[0];
    if (!content) return;

    const resolvedBg = resolveColorIdFromValue(content.backgroundColor, false);
    const resolvedBorder = resolveColorIdFromValue(content.borderColor, true);
    const resolvedButton = resolveColorIdFromValue(content.buttonColor, false);

    form.reset({
      privateTitle: prefillOffer.privateTitle || '',
      title: content.title || '',
      subtitle: content.subTitle || '',
      description: content.description || '',
      buttonText: content.buttonLabel || '',
      buttonLink: content.buttonLink || '',
      showImage: !!content.imageUrl || !!content.imagePath,
      img:
        content.imageUrl || content.imagePath
          ? ((content.imageUrl || content.imagePath) as any)
          : undefined,
      showIcon: !!content.icon,
      icon: content.icon || '',
      backgroundColor: resolvedBg.colorId,
      customBackgroundColor: resolvedBg.customColor,
      borderColor: resolvedBorder.colorId,
      customBorderColor: resolvedBorder.customColor,
      buttonBackgroundColor: resolvedButton.colorId,
      customButtonBackgroundColor: resolvedButton.customColor,
      titleColor: content.titleColor || '#000000',
      textColor: content.textColor || '#000000',
      buttonTextColor: content.buttonTextColor || '#000000',
    });

    setAccordionValue('accordion-1');
  }, [prefillOffer?.id]);

  useEffect(() => {
    if (displayOffer) {
      setAccordionValue(isEvergreen ? '' : 'accordion-1');
    } else {
      setAccordionValue('');
    }
  }, [displayOffer, isEvergreen]);

  const colorsArray = useMemo(
    () => [
      {
        id: 'accent',
        name: t('offerThankyouPage.colors.accent'),
        value: visualIdentity?.accentColor,
        color: visualIdentity?.accentColor,
      },
      {
        id: 'background',
        name: t('offerThankyouPage.colors.background'),
        value: visualIdentity?.backgroundColor,
        color: visualIdentity?.backgroundColor,
      },
      {
        id: 'contrast',
        name: t('offerThankyouPage.colors.contrast'),
        value: visualIdentity?.contrastColor,
        color: visualIdentity?.contrastColor,
      },
      {
        id: 'gradient1-color1',
        name: t('offerThankyouPage.colors.gradient1'),
        value: visualIdentity?.gradient1Color1,
        color: visualIdentity?.gradient1Color1,
      },

      {
        id: 'gradient2-color1',
        name: t('offerThankyouPage.colors.gradient2'),
        value: visualIdentity?.gradient2Color1,
        color: visualIdentity?.gradient2Color1,
      },

      {
        id: 'secondary-background',
        name: t('offerThankyouPage.colors.secondaryBackground'),
        value: visualIdentity?.secondaryBackgroundColor,
        color: visualIdentity?.secondaryBackgroundColor,
      },
      {
        id: 'black',
        name: t('offerThankyouPage.colors.black'),
        value: '#000000',
        color: '#000000',
      },
      {
        id: 'white',
        name: t('offerThankyouPage.colors.white'),
        value: '#FFFFFF',
        color: '#FFFFFF',
      },
    ],
    [t, visualIdentity],
  );

  const borderColorsArray = useMemo(() => [...colorsArray], [colorsArray]);

  const arrayFontColors = useMemo(
    () => [
      {
        name: t('offerThankyouPage.colors.black'),
        value: '#000000',
      },
      {
        name: t('offerThankyouPage.colors.white'),
        value: '#FFFFFF',
      },
    ],
    [t],
  );

  const isCustomColor = (color: string): boolean => {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  };

  const resolveColorIdFromValue = (
    colorValue: string | null | undefined,
    isBorderColor: boolean = false,
  ): { colorId: string; customColor: string } => {
    if (!colorValue || colorValue === 'transparent') {
      return {
        colorId: isBorderColor ? 'no-border' : 'accent',
        customColor: '#000000',
      };
    }

    if (colorValue === 'gradient1') {
      return {
        colorId: 'gradient1-color1',
        customColor: visualIdentity?.gradient1Color1 || '#000000',
      };
    }
    if (colorValue === 'gradient2') {
      return {
        colorId: 'gradient2-color1',
        customColor: visualIdentity?.gradient2Color1 || '#000000',
      };
    }

    if (isCustomColor(colorValue)) {
      if (visualIdentity) {
        if (colorValue === visualIdentity.accentColor) {
          return { colorId: 'accent', customColor: colorValue };
        }
        if (colorValue === visualIdentity.backgroundColor) {
          return { colorId: 'background', customColor: colorValue };
        }
        if (colorValue === visualIdentity.contrastColor) {
          return { colorId: 'contrast', customColor: colorValue };
        }
        if (colorValue === visualIdentity.secondaryBackgroundColor) {
          return { colorId: 'secondary-background', customColor: colorValue };
        }
        if (colorValue === visualIdentity.gradient1Color1) {
          return { colorId: 'gradient1-color1', customColor: colorValue };
        }
        if (colorValue === visualIdentity.gradient2Color1) {
          return { colorId: 'gradient2-color1', customColor: colorValue };
        }
      }

      if (colorValue === '#000000') {
        return { colorId: 'black', customColor: '#000000' };
      }
      if (colorValue === '#FFFFFF') {
        return { colorId: 'white', customColor: '#FFFFFF' };
      }

      return { colorId: 'custom', customColor: colorValue };
    }

    const color = colorsArray.find((c) => c.value === colorValue);
    if (color) {
      return { colorId: color.id, customColor: colorValue };
    }

    return {
      colorId: isBorderColor ? 'no-border' : 'accent',
      customColor: colorValue || '#000000',
    };
  };

  const getColorValueById = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return 'transparent';
    }
    if (colorId === 'custom') {
      return customColor || '#FFFFFF';
    }
    const color = colorsArray.find((c) => c.id === colorId);
    return color?.value || '#FFFFFF';
  };

  const getBackgroundStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'gradient1-color1' || colorId === 'gradient1-color2') {
      return {
        background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1}, ${visualIdentity?.gradient1Color2})`,
      };
    }
    if (colorId === 'gradient2-color1' || colorId === 'gradient2-color2') {
      return {
        background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1} 0.2%, ${visualIdentity?.gradient2Color2} 104.63%)`,
      };
    }
    return {
      backgroundColor: getColorValueById(colorId, customColor),
    };
  };

  const getBorderStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return {
        border: 'none',
      };
    }
    return {
      borderColor: getColorValueById(colorId, customColor),
    };
  };

  const getFinalColor = (
    colorType:
      | 'backgroundColor'
      | 'borderColor'
      | 'titleColor'
      | 'textColor'
      | 'buttonBackgroundColor'
      | 'buttonTextColor'
      | string,
  ) => {
    const typedColorType = colorType as
      | 'backgroundColor'
      | 'borderColor'
      | 'titleColor'
      | 'textColor'
      | 'buttonBackgroundColor'
      | 'buttonTextColor';
    const colorId = form.watch(typedColorType);
    const customColorField =
      `custom${typedColorType.charAt(0).toUpperCase() + typedColorType.slice(1)}` as any;
    const customColor = form.watch(customColorField);

    if (colorId === 'custom') {
      return customColor || '#000000';
    }

    return getColorValueById(colorId || 'accent', customColor);
  };

  const mapFormToContentInput = (formValues: FormValues): OfferTemplateContentInput => {
    const getColorValue = (
      colorType: 'backgroundColor' | 'borderColor' | 'buttonBackgroundColor',
    ): string => {
      const colorId = formValues[colorType];
      const customColorField =
        `custom${colorType.charAt(0).toUpperCase() + colorType.slice(1)}` as keyof FormValues;
      const customColor = formValues[customColorField] as string | undefined;

      if (colorId === 'custom') {
        return customColor || '#000000';
      }

      if (colorId === 'gradient1-color1') {
        return 'gradient1';
      }
      if (colorId === 'gradient2-color1') {
        return 'gradient2';
      }

      return getColorValueById(colorId || 'accent', customColor);
    };

    return {
      title: formValues.title,
      subTitle: formValues.subtitle || null,
      description: formValues.description || null,
      buttonLabel: formValues.buttonText,
      buttonLink: formValues.buttonLink,
      backgroundColor: getColorValue('backgroundColor'),
      borderColor: formValues.borderColor === 'no-border' ? null : getColorValue('borderColor'),
      titleColor: formValues.titleColor || '#000000',
      textColor: formValues.textColor || '#000000',
      buttonColor: getColorValue('buttonBackgroundColor'),
      buttonTextColor: formValues.buttonTextColor || '#000000',
      icon: formValues.showIcon ? formValues.icon || null : null,
      iconColor: null,
      iconBackgroundColor: null,
    };
  };

  const handleSave = useCallback(async () => {
    if (!workspaceId || !webinarIdParam) {
      return;
    }

    const webinarId = parseInt(webinarIdParam, 10);
    if (isNaN(webinarId)) {
      return;
    }

    try {
      setIsSubmitting(true);
      const formValues = form.getValues();

      const isValid = await form.trigger();
      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      let offerId: number;
      const isCreation = !currentOffer;

      if (!currentOffer) {
        const offerInput: OfferTemplateInput = {
          privateTitle: formValues.privateTitle,
          abTesting: false,
          splitPercentA: null,
          thankYouPageOffer: true,
        };

        const createdId = await dispatch(
          createOfferTemplate({
            workspaceId,
            webinarId,
            input: offerInput,
          }),
        ).unwrap();

        if (!createdId) {
          throw new Error('Failed to create offer');
        }

        offerId = createdId;
      } else {
        offerId = currentOffer.id;

        const offerInput: OfferTemplateInput = {
          privateTitle: formValues.privateTitle,
          abTesting: currentOffer.abTesting,
          splitPercentA: currentOffer.splitPercentA,
          thankYouPageOffer: true,
        };

        await dispatch(
          updateOfferTemplate({
            workspaceId,
            webinarId,
            offerId,
            input: offerInput,
          }),
        ).unwrap();
      }

      const contentInput = mapFormToContentInput(formValues);

      const saveContents = async () => {
        await dispatch(
          upsertOfferTemplateContent({
            workspaceId,
            webinarId,
            templateId: offerId,
            contentType: 'A',
            input: contentInput,
          }),
        ).unwrap();

        if (selectedFile && formValues.showImage) {
          await dispatch(
            uploadOfferTemplateContentImage({
              workspaceId,
              webinarId,
              templateId: offerId,
              contentType: 'A',
              file: selectedFile,
            }),
          ).unwrap();
        }
      };

      if (isCreation) {
        const saved = await saveCreatedOffer(saveContents, async () => {
          const rollback = await dispatch(deleteOfferTemplate({ workspaceId, webinarId, offerId }));
          return !deleteOfferTemplate.rejected.match(rollback);
        });
        if (saved.outcome === 'leftOver') {
          console.error(`Offer template ${offerId} left over after a failed creation`, saved.error);
        }
        if (saved.outcome !== 'saved') throw saved.error;
      } else {
        await saveContents();
      }

      const response = await dispatch(
        listOfferTemplates({ workspaceId, webinarId, page: 0, size: 50 }),
      ).unwrap();
      const updatedOffer = response.find((offer) => offer.thankYouPageOffer === true);
      if (updatedOffer) {
        setCurrentOffer(updatedOffer);
        await onOfferSaved?.(updatedOffer.id);
      }
      toast.success(t('offersForm.offerUpdatedSuccess'));
      setAccordionValue('');
    } catch (error) {
      console.error('Error saving thank you offer:', error);
      const err = error as { message?: string; code?: string; status?: number };
      const errorMessage = err?.message || err?.code || t('offerThankyouPage.saveError');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [workspaceId, webinarIdParam, currentOffer, form, dispatch, selectedFile, onOfferSaved]);

  // const handleDelete = useCallback(() => {
  //   if (!workspaceId || !webinarIdParam || !currentOffer) {
  //     return;
  //   }

  //   const webinarId = parseInt(webinarIdParam, 10);
  //   if (isNaN(webinarId)) {
  //     return;
  //   }

  //   try {
  //     const templateId = currentOffer.id;
  //     if (!templateId) {
  //       return;
  //     }

  //     if (currentOffer.contents && currentOffer.contents.length > 0) {
  //       const content = currentOffer.contents[0];
  //       if (content.type) {
  //         dispatch(
  //           deleteOfferTemplateContent({
  //             workspaceId,
  //             webinarId,
  //             templateId,
  //             contentType: content.type,
  //           }),
  //         ).unwrap();
  //       }
  //     }

  //     dispatch(
  //       deleteOfferTemplate({
  //         workspaceId,
  //         webinarId,
  //         offerId: templateId,
  //       }),
  //     ).unwrap();

  //     form.reset({
  //       privateTitle: '',
  //       title: '',
  //       subtitle: '',
  //       description: '',
  //       buttonText: '',
  //       buttonLink: '',
  //       showImage: false,
  //       img: undefined,
  //       showIcon: false,
  //       icon: '',
  //       backgroundColor: 'accent',
  //       customBackgroundColor: '#000000',
  //       borderColor: 'accent',
  //       customBorderColor: '#000000',
  //       buttonBackgroundColor: 'accent',
  //       customButtonBackgroundColor: '#000000',
  //       titleColor: '#000000',
  //       textColor: '#000000',
  //       buttonTextColor: '#000000',
  //     });

  //     form.setValue('img', undefined);
  //     form.setValue('showImage', false);

  //     setSelectedFile(null);
  //     setCurrentOffer(null);
  //   } catch (error) {
  //     console.error('Error deleting offer:', error);
  //   }
  // }, [workspaceId, webinarIdParam, currentOffer, dispatch, form]);

  if (isLoading) {
    return (
      <div>
        <Accordion
          type="single"
          collapsible
          defaultValue={isEvergreen ? undefined : 'item-1'}
          className="bg-medium rounded-[20px] p-4"
        >
          <AccordionItem value="item-1" className="bg-medium">
            <div className="flex flex-col w-full">
              <div className="flex items-center gap-2 w-full">
                <div className="flex-1">
                  <Skeleton className="h-10 w-full" />
                </div>
                <AccordionTrigger className="rounded-[20px] p-0" />
              </div>
            </div>
            <AccordionContent className="flex flex-col gap-4 rounded-[20px] p-0 pt-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-6 w-40" />
              <div className="grid lg:grid-cols-2 gap-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
              <Skeleton className="h-6 w-40" />
              <div className="grid lg:grid-cols-2 gap-4">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
              <Skeleton className="h-10 w-full" />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  return (
    <Form {...form}>
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={setAccordionValue}
        className="bg-medium rounded-[20px] p-4"
      >
        <AccordionItem value="accordion-1" className="bg-medium">
          <div className="flex flex-col  w-full">
            <div className="flex items-start gap-2 w-full">
              <div className="flex-1">
                <FormField
                  name="privateTitle"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <TextFieldWithLimit
                        label={t('offerThankyouPage.privateTitleLabel')}
                        value={field.value ?? ''}
                        onChange={(v) => field.onChange(v)}
                        className="bg-white"
                        error={fieldState.error?.message}
                      />
                    </FormItem>
                  )}
                />
              </div>
              {/* {currentOffer && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="bg-white rounded-lg mr-4 py-3 px-2 border border-input cursor-pointer"
                >
                  <Trash2 className="size-6" />
                </button>
              )} */}
              <AccordionTrigger
                className=" rounded-[20px] p-0 h-14 block"
                {...(currentOffer ? { fillForm: true } : {})}
              />
            </div>
          </div>

          <AccordionContent className="flex flex-col gap-4 rounded-[20px] p-0 pt-4">
            <Title level={2}>{t('offerThankyouPage.offerTitle')}</Title>

            <FormField
              name="title"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <TextFieldWithLimit
                    label={t('offerThankyouPage.titleLabel')}
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v)}
                    maxLength={35}
                    className="bg-white"
                    error={fieldState.error?.message}
                  />
                </FormItem>
              )}
            />

            <FormField
              name="subtitle"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <TextFieldWithLimit
                    label={t('offerThankyouPage.subtitleLabel')}
                    className="bg-white"
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v)}
                    maxLength={50}
                    error={fieldState.error?.message}
                  />
                </FormItem>
              )}
            />
            <FormField
              name="description"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem>
                  <TextFieldWithLimit
                    label={t('offerThankyouPage.descriptionLabel')}
                    className="bg-white"
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v)}
                    maxLength={100}
                    error={fieldState.error?.message}
                  />
                </FormItem>
              )}
            />

            <Title level={2}>{t('offerThankyouPage.buttonSectionTitle')}</Title>

            <div className="flex items-center gap-4 w-full flex-col">
              <FormField
                name="buttonText"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormItem className="w-full">
                    <TextFieldWithLimit
                      label={t('offerThankyouPage.buttonTextLabel')}
                      className="bg-white w-full "
                      value={field.value ?? ''}
                      onChange={(v) => field.onChange(v)}
                      maxLength={35}
                      error={fieldState.error?.message}
                    />
                  </FormItem>
                )}
              />

              <FormField
                name="buttonLink"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <UrlVariableInput
                      label={t('offerThankyouPage.buttonLinkLabel')}
                      className="w-full"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                    />
                    {field.value && !field.value.trim().startsWith('https://') ? (
                      <p className="text-left text-red-500 text-sm">
                        {t('offerThankyouPage.validation.buttonLinkInvalidUrl')}
                      </p>
                    ) : (
                      <FormMessage className="text-left text-red-500 text-sm" />
                    )}
                  </FormItem>
                )}
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <Label>{t('offerThankyouPage.showImageLabel')}</Label>
              <Switch
                checked={form.watch('showImage')}
                onCheckedChange={(checked) => {
                  form.setValue('showImage', checked);
                  form.trigger('img');
                }}
              />
            </div>

            <FormField
              name="img"
              control={form.control}
              render={() => (
                <FormItem>
                  <div className="relative">
                    <input
                      type="file"
                      id="file-upload"
                      className="sr-only"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSelectedFile(file);
                          form.setValue('img', file);
                          form.setValue('showImage', true);
                        }
                      }}
                    />
                    <Label
                      htmlFor="file-upload"
                      className="p-4 bg-white rounded-2xl flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="size-10 bg-medium rounded-lg flex items-center justify-center">
                          <img
                            src="/assets/attach.svg"
                            alt={t('offerThankyouPage.fileInput.attachIconAlt')}
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <span>
                            {selectedFile
                              ? selectedFile.name
                              : form.watch('img') && typeof form.watch('img') === 'string'
                                ? t('offerThankyouPage.fileInput.existingFile')
                                : t('offerThankyouPage.fileInput.selectFile')}
                          </span>
                          <p className="text-sm text-gray-tertiary">
                            {selectedFile
                              ? `${(selectedFile.size / 1024 / 1024).toFixed(1)}mb`
                              : form.watch('img') && typeof form.watch('img') === 'string'
                                ? t('offerThankyouPage.fileInput.existingFileDescription')
                                : t('offerThankyouPage.fileInput.noFileSelected')}
                          </p>
                        </div>
                      </div>
                    </Label>
                    {(selectedFile ||
                      (form.watch('img') && typeof form.watch('img') === 'string')) && (
                      <Trash2
                        className="cursor-pointer absolute top-1/2 -translate-y-1/2  right-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          form.setValue('img', undefined);
                        }}
                      />
                    )}
                  </div>
                  <FormMessage className="text-left text-red-500 text-sm" />
                </FormItem>
              )}
            />

            {/* <div className="flex items-center justify-between ">
              <Label>{t('offerThankyouPage.showIconLabel')}</Label>
              <Switch
                checked={form.watch('showIcon')}
                onCheckedChange={(checked) => {
                  form.setValue('showIcon', checked);
                  form.trigger('icon');
                }}
              />
            </div> */}

            {/* 
            <FormField
              name="icon"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger label={t('offerThankyouPage.iconSelectLabel')} className="h-14">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">
                        <BadgePercent size={48} className="size-6" />
                      </SelectItem>
                      <SelectItem value="award">
                        <Award size={48} className="size-6" />
                      </SelectItem>
                      <SelectItem value="trophy">
                        <Trophy size={48} className="size-6" />
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            /> */}

            <div className="flex flex-col pt-8 space-y-4">
              <Title level={2}>{t('offerThankyouPage.colorsTitle')}</Title>

              <div className="grid lg:grid-cols-2 gap-4">
                {/* background */}
                <ColorSelector
                  form={form}
                  offerType="A"
                  colorsArray={colorsArray}
                  colorFieldName="backgroundColor"
                  customColorFieldName="customBackgroundColor"
                  label={t('offerThankyouPage.backgroundColorLabel')}
                  customColorLabel={t('offerThankyouPage.colors.custom')}
                  getFinalColor={getFinalColor}
                  getStyle={getBackgroundStyle}
                />

                {/* Borders */}
                <ColorSelector
                  form={form}
                  offerType="A"
                  colorsArray={borderColorsArray}
                  colorFieldName="borderColor"
                  customColorFieldName="customBorderColor"
                  label={t('offerThankyouPage.borderColorLabel')}
                  customColorLabel={t('offerThankyouPage.colors.custom')}
                  noBorderLabel={t('offerThankyouPage.colors.noBorder')}
                  showNoBorder={true}
                  getFinalColor={getFinalColor}
                  getStyle={(colorId, customColor) => {
                    if (colorId === 'no-border') {
                      return { backgroundColor: 'white' };
                    }
                    return getBackgroundStyle(colorId, customColor);
                  }}
                />

                {/* Title */}
                <ColorSelector
                  form={form}
                  offerType="A"
                  colorsArray={arrayFontColors.map((color) => ({
                    id: color.value,
                    name: color.name,
                    value: color.value,
                  }))}
                  colorFieldName="titleColor"
                  label={t('offerThankyouPage.titleColorLabel')}
                  showCustomColor={false}
                  defaultValue="#000000"
                />

                {/* Text */}
                <ColorSelector
                  form={form}
                  offerType="A"
                  colorsArray={arrayFontColors.map((color) => ({
                    id: color.value,
                    name: color.name,
                    value: color.value,
                  }))}
                  colorFieldName="textColor"
                  label={t('offerThankyouPage.textColorLabel')}
                  showCustomColor={false}
                  defaultValue="#000000"
                />

                {/* couleur du fond bouton*/}
                <ColorSelector
                  form={form}
                  offerType="A"
                  colorsArray={colorsArray}
                  colorFieldName="buttonBackgroundColor"
                  customColorFieldName="customButtonBackgroundColor"
                  label={t('offerThankyouPage.buttonBackgroundColorLabel')}
                  customColorLabel={t('offerThankyouPage.colors.custom')}
                  getFinalColor={getFinalColor}
                  getStyle={getBackgroundStyle}
                />

                {/* Text button */}
                <ColorSelector
                  form={form}
                  offerType="A"
                  colorsArray={arrayFontColors.map((color) => ({
                    id: color.value,
                    name: color.name,
                    value: color.value,
                  }))}
                  colorFieldName="buttonTextColor"
                  label={t('offerThankyouPage.buttonTextColorLabel')}
                  showCustomColor={false}
                  defaultValue="#000000"
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 pt-8">
              <Title level={2}>{t('offerThankyouPage.previewTitle')}</Title>

              <div className="flex items-center justify-center">
                <div
                  className="border-2 p-4 rounded-[20px] flex flex-col gap-2 w-[408px] "
                  style={{
                    ...getBackgroundStyle(
                      form.watch('backgroundColor') || 'accent',
                      form.watch('customBackgroundColor'),
                    ),
                    ...getBorderStyle(
                      form.watch('borderColor') || 'accent',
                      form.watch('customBorderColor'),
                    ),
                  }}
                >
                  {/* icon */}
                  <div className="flex items-center justify-center">
                    {form.watch('showIcon') && form.watch('icon') && (
                      <>
                        {form.watch('icon') === 'percent' && (
                          <BadgePercent
                            color={
                              getBackgroundStyle(form.watch('borderColor') || 'accent')
                                .backgroundColor
                            }
                            size={48}
                            className="size-6"
                          />
                        )}
                        {form.watch('icon') === 'award' && (
                          <Award
                            color={
                              getBackgroundStyle(form.watch('borderColor') || 'accent')
                                .backgroundColor
                            }
                            size={48}
                            className="size-6"
                          />
                        )}
                        {form.watch('icon') === 'trophy' && (
                          <Trophy
                            color={
                              getBackgroundStyle(form.watch('borderColor') || 'accent')
                                .backgroundColor
                            }
                            size={48}
                            className="size-6"
                          />
                        )}
                      </>
                    )}
                  </div>

                  {/* title */}
                  <div className="flex items-center justify-center">
                    <Title level={2}>
                      <span
                        style={{
                          color: form.watch('titleColor') || '#000000',
                        }}
                      >
                        {form.watch('title')}
                      </span>
                    </Title>
                  </div>

                  {/* subtitle */}
                  <div className="flex items-center justify-center">
                    <p
                      className="text-sm"
                      style={{
                        color: form.watch('textColor') || '#000000',
                      }}
                    >
                      {form.watch('subtitle')}
                    </p>
                  </div>

                  {/* description */}
                  <div className="flex items-center justify-center">
                    <p
                      className="text-sm"
                      style={{
                        color: form.watch('textColor') || '#000000',
                      }}
                    >
                      {form.watch('description')}
                    </p>
                  </div>

                  {form.watch('showImage') && (
                    <>
                      {selectedFile && (
                        <img
                          src={URL.createObjectURL(selectedFile)}
                          alt={t('offerThankyouPage.previewImageAlt')}
                          className="rounded-lg w-full h-full max-h-56 object-contain"
                        />
                      )}
                      {!selectedFile && form.watch('img') && form.watch('img') instanceof File && (
                        <img
                          src={URL.createObjectURL(form.watch('img') as File)}
                          alt={t('offerThankyouPage.previewImageAlt')}
                          className="rounded-lg w-full h-full max-h-56 object-contain"
                        />
                      )}
                      {!selectedFile &&
                        form.watch('img') &&
                        typeof form.watch('img') === 'string' &&
                        (form.watch('img') as string).trim() !== '' && (
                          <img
                            src={form.watch('img') as string}
                            alt={t('offerThankyouPage.previewImageAlt')}
                            className="rounded-lg w-full h-full max-h-56 object-contain"
                          />
                        )}
                    </>
                  )}

                  {/* button */}
                  <div className="flex items-center justify-center">
                    <Button
                      style={{
                        ...getBackgroundStyle(
                          form.watch('buttonBackgroundColor') || 'accent',
                          form.watch('customButtonBackgroundColor'),
                        ),
                        color: form.watch('buttonTextColor') || '#000000',
                      }}
                    >
                      {form.watch('buttonText')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {!(prefillOffer && !form.formState.isDirty) && (
              <div className="w-full flex items-center justify-center">
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={
                    isSubmitting ||
                    !form.formState.isValid ||
                    ((!!prefillOffer || !!currentOffer) && !form.formState.isDirty)
                  }
                  className="w-full disabled:bg-gray-200 disabled:text-gray-500"
                >
                  {isSubmitting && <Loader />}
                  {t('save')}
                </Button>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Form>
  );
}
